import React from 'react';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Icon from '@material-ui/core/Icon';
import Input from '@material-ui/core/Input';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { /*Link as RouterLink,*/ RouteComponentProps } from 'react-router-dom';
// Components
import App from '../../../components/app';
import { Dialog } from '../../../components/dialog';
import EnhancedTable from '../../../components/table';
import ErrorMessage from '../../../components/error-message';
import { TooltipButton } from '../../../components/tooltip-button';

// Modules
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { actions as partnerBillActions, partnerBillStatus } from '../../../redux/reducers/partner-bills';
import { makeMemoizedGetDatas, tableRequestPoints } from '../../../redux/reducers/table';
import { BillPartner } from '../../../redux/reducers/api-types';
// Ressources
import DateUtils from '../../../utils/dateUtils';
import FormattingUtils from '../../../utils/formattingUtils';
import withGlobalStyles from '../../../utils/global-styles';
import styles from './partner-bills-pending-style';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { generateOptionsString } from '../../../utils/businessUtils';
import { Cell, createCell } from '../../../components/table/table-cell-creator';
import { clsx } from '../../../utils/htmlUtils';
import { FormatType, HeadCell, PickerOption } from '../../../local-types';

interface PartnerBillsPendingProps extends WithTranslation, WithStyles, RouteComponentProps {
    globalClasses: any;
    partnerBillsList: any;
    updatePartnerBillsPendingError: ErrorPayload;
    bulkActionsPartnerBillsPendingError: ErrorPayload;
    exportPaiementPartnerBillsPendingError: ErrorPayload;
    updatePartnerBillStatus: (id: string, status: any, callback: React.EffectCallback) => void;
    bulkValidate: (payload: { start_date: string; end_date: string }, callback: React.EffectCallback) => void;
    bulkSend: (payload: { start_date: string; end_date: string }, callback: React.EffectCallback) => void;
    exportPaiement: (payload: { start_date: string; end_date: string }) => void;
}

const CONST_EXPORT: string = 'export';
class PartnerBillsPendingPage extends React.Component<PartnerBillsPendingProps> {
    tableRef: any;

    isAdmin: boolean;

    constructor(props) {
        super(props);
        this.isAdmin = UserUtils.checkRightsOrAdmin({ name: AccountRight.PARTNER_BILLING_MGMT, mode: RightRank.W });
    }

    //========================================//
    //=============== Handlers ===============//
    //========================================//

    handleStatus = (row: any, status: string) => () => {
        this.props.updatePartnerBillStatus(row.id, status, () => {
            this.tableRef.reload();
        });
    };

    /**
     * Handle the different actions considering the date given and the newStatus
     * @param startDate Start of the period -> end is auto calculated 1 week later
     * @param newStatus partnerBillStatus ou CONST_EXPORT
     */
    handleStatuses = (startDate: Date, newStatus: string) => {
        // console.log(startDate, newStatus);
        if (startDate) {
            const start_date = new Date(startDate);
            start_date.setHours(0);
            const nextWeek = DateUtils.getNextWeek(start_date);
            nextWeek.setHours(0);
            const payload = {
                start_date: DateUtils.dateFormat(start_date.getTime(), 'isoDateTime'),
                end_date: DateUtils.dateFormat(nextWeek.getTime(), 'isoDateTime'),
            };
            if (newStatus === partnerBillStatus.VALIDATED) {
                this.props.bulkValidate(payload, () => {
                    this.tableRef.reload();
                });
            } else if (newStatus === partnerBillStatus.SENT) {
                this.props.bulkSend(payload, () => {
                    this.tableRef.reload();
                });
            } else if (newStatus === CONST_EXPORT) {
                this.props.exportPaiement(payload);
            }
        }
    };

    //========================================//
    //=============== Builders ===============//
    //========================================//

    generateStatusOptions = (): PickerOption[] => {
        return generateOptionsString(this.props.t, ['pending', 'blocked', 'validated'], 'partner_bill_status.');
    };

    generateFilters = (): any => {
        const filterParams: any = {
            'in[status]': ['pending', 'blocked', 'validated'],
        };
        return filterParams;
    };

    generateHeaders = (): HeadCell[] => {
        const { t, globalClasses, classes } = this.props;
        const statusOptions: HeadCell[] = [
            createCell(t, Cell.BILL_NUMBER),
            createCell(t, Cell.BILL_DOCUMENT_PATH),
            createCell(t, Cell.PARTNER_ID),
            createCell(t, Cell.AMOUNT_WITH_TAX, { label: t('amount_due'), filterable: false }),
            createCell(t, Cell.STATUS, { pickerOptions: this.generateStatusOptions() }),
            createCell(t, Cell.PERIOD),
        ];

        if (this.isAdmin) {
            statusOptions.push({
                id: 'action',
                label: t('common:action'),
                sortable: false,
                filterable: false,
                noLink: true,
                cellProps: {
                    className: classes.actionCell,
                },
                formatCell: (row: BillPartner) => {
                    return (
                        <div>
                            {row.status === partnerBillStatus.PENDING && (
                                <>
                                    {row.amount_with_tax >= 0 && (
                                        <Dialog
                                            btnTitle={t('common:validate')}
                                            description={t('validate_bill_desc')}
                                            customButton={
                                                <TooltipButton title={t('common:validate')}>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        className={clsx(
                                                            globalClasses.btnAutoWidth,
                                                            globalClasses.btnSuccess,
                                                        )}
                                                    >
                                                        <Icon fontSize="small">check</Icon>
                                                    </Button>
                                                </TooltipButton>
                                            }
                                            onOK={this.handleStatus(row, partnerBillStatus.VALIDATED)}
                                        />
                                    )}
                                    <Dialog
                                        btnTitle={t('blocked')}
                                        description={t('blocked_bill_desc')}
                                        customButton={
                                            <TooltipButton title={t('blocked')}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    className={clsx(
                                                        globalClasses.btnAutoWidth,
                                                        globalClasses.btnDanger,
                                                    )}
                                                >
                                                    <Icon fontSize="small">close</Icon>
                                                </Button>
                                            </TooltipButton>
                                        }
                                        onOK={this.handleStatus(row, partnerBillStatus.BLOCKED)}
                                    />
                                </>
                            )}
                            {row.status === partnerBillStatus.VALIDATED && (
                                <>
                                    {/* <Dialog
                                            btnTitle={t('mark_as_paid')}
                                            description={t('mark_as_paid_desc')}
                                            customButton={
                                                <TooltipButton title={t('mark_as_paid')}>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        className={globalClasses.btnAutoWidth}
                                                    >
                                                        <Icon fontSize="small">euro</Icon>
                                                    </Button>
                                                </TooltipButton>
                                            }
                                            onOK={this.handleStatus(row, partnerBillStatus.SENT)}
                                        /> */}
                                    <Dialog
                                        btnTitle={t('blocked')}
                                        description={t('blocked_bill_desc')}
                                        customButton={
                                            <TooltipButton title={t('blocked')}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    className={clsx(
                                                        globalClasses.btnAutoWidth,
                                                        globalClasses.btnDanger,
                                                    )}
                                                >
                                                    <Icon fontSize="small">close</Icon>
                                                </Button>
                                            </TooltipButton>
                                        }
                                        onOK={this.handleStatus(row, partnerBillStatus.BLOCKED)}
                                    />
                                </>
                            )}
                            {row.status === partnerBillStatus.BLOCKED && row.amount_with_tax >= 0 && (
                                <Dialog
                                    btnTitle={t('common:validate')}
                                    description={t('validate_bill_desc')}
                                    customButton={
                                        <TooltipButton title={t('common:validate')}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                className={clsx(globalClasses.btnAutoWidth, globalClasses.btnSuccess)}
                                            >
                                                <Icon fontSize="small">check</Icon>
                                            </Button>
                                        </TooltipButton>
                                    }
                                    onOK={this.handleStatus(row, partnerBillStatus.VALIDATED)}
                                />
                            )}
                        </div>
                    );
                },
            });
        }
        return statusOptions;
    };

    renderSelectionActions = () => {
        const { t, globalClasses } = this.props;
        const prevWeek = DateUtils.getPreviousWeek();
        const defaultMonth = DateUtils.dateFormat(DateUtils.getMonday(prevWeek).getTime(), 'yyyy-mm-dd');

        return (
            this.isAdmin && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <div style={{ marginRight: 20 }}>{t('bulk_action')}</div>
                    <Dialog
                        btnTitle={t('bulk_validate')}
                        description={t('bulk_validate_desc')}
                        customButton={
                            <TooltipButton title={t('bulk_validate_desc')}>
                                <Button
                                    startIcon={<Icon>check</Icon>}
                                    size="small"
                                    variant="contained"
                                    className={globalClasses.btnSuccess}
                                >
                                    {t('bulk_validate')}
                                </Button>
                            </TooltipButton>
                        }
                        formComponent={
                            <>
                                <FormControl key={'formStartDate'} style={{ display: 'flex' }}>
                                    <div>{t('period_choice')}</div>
                                    <Input
                                        type="date"
                                        name={'start_date'}
                                        required={true}
                                        defaultValue={defaultMonth}
                                    />
                                </FormControl>
                            </>
                        }
                        onOK={(fields) => this.handleStatuses(fields.start_date, partnerBillStatus.VALIDATED)}
                    />
                    <div style={{ marginLeft: 20 }} />
                    <Dialog
                        btnTitle={t('transactions.export')}
                        description={t('transactions.button')}
                        customButton={
                            <TooltipButton title={t('transactions.button')}>
                                <Button
                                    startIcon={<Icon>description</Icon>}
                                    size="small"
                                    variant="contained"
                                    color="secondary"
                                >
                                    {t('transactions.export')}
                                </Button>
                            </TooltipButton>
                        }
                        formComponent={
                            <>
                                <FormControl key={'formStartDate'} style={{ display: 'flex' }}>
                                    <div>{t('period_choice')}</div>
                                    <Input
                                        type="date"
                                        name={'start_date'}
                                        required={true}
                                        defaultValue={defaultMonth}
                                    />
                                </FormControl>
                            </>
                        }
                        onOK={(fields) => this.handleStatuses(fields.start_date, CONST_EXPORT)}
                    />
                    <div style={{ marginLeft: 20 }} />
                    <Dialog
                        btnTitle={t('bulk_mark_as_paid')}
                        description={t('bulk_mark_as_paid_desc')}
                        customButton={
                            <TooltipButton title={t('bulk_mark_as_paid_desc')}>
                                <Button startIcon={<Icon>mail</Icon>} size="small" variant="contained" color="primary">
                                    {t('bulk_mark_as_paid')}
                                </Button>
                            </TooltipButton>
                        }
                        formComponent={
                            <>
                                <FormControl key={'formStartDate'} style={{ display: 'flex' }}>
                                    <div>{t('period_choice')}</div>
                                    <Input
                                        type="date"
                                        name={'start_date'}
                                        required={true}
                                        defaultValue={defaultMonth}
                                    />
                                </FormControl>
                            </>
                        }
                        onOK={(fields) => this.handleStatuses(fields.start_date, partnerBillStatus.SENT)}
                    />
                </div>
            )
        );
    };

    render() {
        const {
            t,
            updatePartnerBillsPendingError,
            bulkActionsPartnerBillsPendingError,
            exportPaiementPartnerBillsPendingError,
        } = this.props;

        return (
            <App
                needRights={AccountRight.PARTNER_BILLING_MGMT}
                disableBreadcrumb
                header={{
                    title: t('pending.title'),
                }}
            >
                <ErrorMessage payload={updatePartnerBillsPendingError} />
                <ErrorMessage payload={bulkActionsPartnerBillsPendingError} />
                <ErrorMessage payload={exportPaiementPartnerBillsPendingError} />
                {this.renderSelectionActions()}
                <EnhancedTable
                    onRef={(ref) => (this.tableRef = ref)}
                    headers={this.generateHeaders()}
                    type={tableRequestPoints.PARTNER_BILLS_BETA}
                    filterParams={this.generateFilters()}
                    filterVisible={false}
                />
            </App>
        );
    }
}

const PartnerBillsPendingPageWithTranslation = withGlobalStyles(
    withStyles(styles)(withTranslation(['partner_bill', 'common'])(PartnerBillsPendingPage)),
);

const makemapStateToProps = () => {
    const getDatas = makeMemoizedGetDatas();

    const mapStateToProps = (state) => {
        return {
            partnerBillsList: getDatas(state, { type: { reducer: 'partner_bills_list' } }),
            updatePartnerBillsPendingError: errorSelectors.updatePartnerBillsPendingError(state),
            exportPaiementPartnerBillsPendingError: errorSelectors.exportPaiementPartnerBillsPendingError(state),
            bulkActionsPartnerBillsPendingError: errorSelectors.bulkActionsPartnerBillsPendingError(state),
        };
    };
    return mapStateToProps;
};

const mapDispatchToProps = {
    updatePartnerBillStatus: partnerBillActions.updatePartnerBillStatus,
    bulkValidate: partnerBillActions.bulkValidate,
    bulkSend: partnerBillActions.bulkSend,
    exportPaiement: partnerBillActions.exportPaiement,
};

export default connect(makemapStateToProps, mapDispatchToProps)(PartnerBillsPendingPageWithTranslation);
